/**
 * Template-side adapter around the campaign-side `getStepDisplay`. The
 * template editor has no per-contact state (`error`, `state`,
 * `isDifferentFromTemplate`), so this thin re-export lets templates pass
 * `SvrCadenceTemplateStep`-shaped data through the same renderer as the
 * campaign rows. Variable colouring and segment parsing are identical.
 */
import type { CampaignDayCardStep, StepDisplay } from '../CampaignDayCard/stepDisplayInfo';
import { getStepDisplay } from '../CampaignDayCard/stepDisplayInfo';

export type TemplateBlockCardStep = Pick<
  CampaignDayCardStep,
  | 'id'
  | 'type'
  | 'body2'
  | 'subject'
  | 'postsToLike'
  | 'cc'
  | 'bcc'
  | 'siftConditions'
  | 'onlyOpenLink'
  | 'autoWithdraw'
> & {
  /**
   * Server-computed validation error for the step content. Surfaced as
   * the red error tint on the row, mirroring the campaign-side `error`
   * field but sourced from `cadence_template_step.computedError`.
   */
  error?: string | null;

  /**
   * When true, this message/inmail/email step's body (and subject for
   * inmail/email) is drafted per contact by Claude at the candidate stage
   * using `personalizationPrompt` as the instruction. The static body /
   * subject fields are not sent — they're replaced by the approved draft
   * at promotion.
   */
  isPersonalized?: boolean | null;
  personalizationPrompt?: string | null;

  /**
   * Touch-campaign step chaining: PUBLIC ids of the campaigns to enroll the
   * contact into when this step completes (empty when none). Surfaced in the
   * editor's "When this step completes" picker.
   */
  triggerCadenceTemplateIds?: string[];
};

export function getTemplateStepDisplay(step: TemplateBlockCardStep): StepDisplay {
  // When the step is flagged for per-contact AI personalization, the
  // static body / subject / cc / bcc are irrelevant for the read-only row
  // — at send time Claude drafts those per contact. Surface the prompt
  // instead so the rep can see what AI is told to write.
  if (step.isPersonalized) {
    const prompt = (step.personalizationPrompt ?? '').trim();
    const typeLabel =
      step.type === 'inmail' ? 'InMail' : step.type === 'email' ? 'email' : 'message';
    return {
      primary: `AI will draft this ${typeLabel} per contact`,
      extras: [
        {
          label: 'Prompt',
          value: prompt.length > 0 ? prompt : 'No prompt set yet',
        },
      ],
    };
  }
  return getStepDisplay(step);
}
